// src/routes/Rankings.tsx
//
// player-value-rankings Phase 3 — the user-visible slice. Renders the
// precomputed `playerValues` pool (rank-ordered by the daily refresh) with
// client-side position/franchise filters and a per-row value breakdown.
import { useConvexAuth, useQuery } from 'convex/react';
import { useMemo, useState } from 'react';
import { api } from '../../convex/_generated/api';
import type { Doc } from '../../convex/_generated/dataModel';
import {
  CONTRACT_FACTOR_PER_YEAR,
  MIN_STARTS_FOR_RATE,
  REPLACEMENT_BUFFER,
  TOP_ANCHOR_BONES,
} from '../../convex/lib/valueMath';
import { type Column, DataTable } from '../components/DataTable';
import { Empty } from '../components/Empty';
import { Loading } from '../components/Loading';
import { PageShell } from '../components/PageShell';
import { PlayerLink } from '../components/PlayerLink';

const POSITIONS = ['ALL', 'QB', 'RB', 'WR', 'TE', 'K', 'DEF'] as const;

/** Two ranking lenses over the same rows, BOTH priced in bones and shown
 *  side by side (commissioner direction 2026-08-27): Market (KTC-superflex
 *  basis) and VORP (the league's own lineup scarcity). Clicking a column
 *  header re-ranks the list by that lens. */
type View = 'market' | 'vorp';

type Row = Doc<'playerValues'>;

export default function Rankings(): JSX.Element {
  const { isLoading: authLoading, isAuthenticated } = useConvexAuth();
  // Authed queries throw UNAUTHENTICATED for signed-out visitors; skip them
  // until a session exists so the page degrades to a sign-in prompt instead.
  const rows = useQuery(api.rankings.db.getRankings, isAuthenticated ? {} : 'skip');
  const franchises = useQuery(api.franchises.listFranchises, isAuthenticated ? {} : 'skip');
  const [pos, setPos] = useState<(typeof POSITIONS)[number]>('ALL');
  const [franchiseFilter, setFranchiseFilter] = useState<string>('ALL'); // 'ALL' | 'FA' | franchiseId
  const [view, setView] = useState<View>('market');
  const [expanded, setExpanded] = useState<string | null>(null); // nflId

  // Rows arrive rank-sorted from the server; filters are pure client-side.
  // The VORP lens re-sorts by vorpRank (rows without one — pre-VORP data —
  // keep their market order at the tail).
  const filtered = useMemo(() => {
    const kept = (rows ?? []).filter((r) => {
      if (pos !== 'ALL' && r.position !== pos) return false;
      if (franchiseFilter === 'FA') return r.teamName === undefined;
      if (franchiseFilter !== 'ALL') return String(r.franchiseId ?? '') === franchiseFilter;
      return true;
    });
    if (view !== 'vorp') return kept;
    return [...kept].sort((a, b) => (a.vorpRank ?? 1e9 + a.rank) - (b.vorpRank ?? 1e9 + b.rank));
  }, [rows, pos, franchiseFilter, view]);

  if (!authLoading && !isAuthenticated) {
    return (
      <PageShell title="Rankings">
        <Empty>Sign in to view player rankings.</Empty>
      </PageShell>
    );
  }
  if (rows === undefined || franchises === undefined) {
    return (
      <PageShell title="Rankings">
        <Loading />
      </PageShell>
    );
  }
  if (rows.length === 0) {
    return (
      <PageShell title="Rankings">
        <Empty>No rankings computed yet — the daily refresh has not run.</Empty>
      </PageShell>
    );
  }

  const sortedFranchises = [...franchises].sort((a, b) => a.name.localeCompare(b.name));
  const computedAt = new Date(rows[0].computedAt).toLocaleString();
  // Lens vintage: VORP is computed from completed seasons only; every row
  // carries the same vorpSeason (undefined on pre-VORP data).
  const vorpSeason = rows[0].vorpSeason;
  const vorpLabel = vorpSeason !== undefined ? `VORP — through ${vorpSeason}` : 'VORP';

  const sortHeader = (id: View, label: string) => (
    <button
      type="button"
      onClick={() => setView(id)}
      className={`inline-flex items-center gap-0.5 ${
        view === id ? 'text-gray-900 underline underline-offset-2' : 'text-gray-500'
      }`}
      title={
        id === 'market'
          ? 'KeepTradeCut superflex dynasty values, rescaled to bones. Click to rank by this column.'
          : `What a player's production was worth over a replacement starter in BDL lineups${
              vorpSeason !== undefined ? ` (through ${vorpSeason})` : ''
            }, times contract years. Click to rank by this column.`
      }
    >
      {label}
      {view === id ? ' ▾' : ''}
    </button>
  );

  const columns: Column<Row>[] = [
    {
      header: '#',
      cell: (r) => (view === 'vorp' ? (r.vorpRank ?? '—') : r.rank),
      align: 'right',
    },
    {
      header: 'Player',
      cell: (r) =>
        r.playerId ? (
          <PlayerLink playerId={r.playerId} name={r.name} />
        ) : (
          <span>{r.name}</span> // graph-only player — no app row, no link
        ),
    },
    {
      header: 'Pos',
      cell: (r) => (
        <>
          {r.position}
          {pos !== 'ALL' && (
            <span className="text-xs text-gray-500 ml-1">
              {r.position}
              {view === 'vorp' ? (r.vorpPosRank ?? '—') : r.posRank}
            </span>
          )}
        </>
      ),
    },
    { header: 'Age', cell: (r) => r.age ?? '—', align: 'right' },
    {
      header: 'Team',
      cell: (r) => r.teamName ?? <span className="text-gray-400">FA</span>,
    },
    { header: 'Yrs', cell: (r) => r.contractYears, align: 'right' },
    { header: 'wPPG', cell: (r) => r.wppg.toFixed(2), align: 'right' },
    {
      header: sortHeader('market', 'Market'),
      cell: (r) => (
        <span className={view === 'market' ? 'font-semibold' : 'text-gray-500'}>
          {r.bones ?? '—'}
        </span>
      ),
      align: 'right',
    },
    {
      header: sortHeader('vorp', 'VORP'),
      cell: (r) => (
        <span className={view === 'vorp' ? 'font-semibold' : 'text-gray-500'}>
          {r.vorpBones ?? '—'}
        </span>
      ),
      align: 'right',
    },
  ];

  const breakdown = (r: Row) =>
    expanded === r.nflId ? (
      <div className="text-xs text-gray-500">
        <div>
          Market {r.bones ?? '—'} bones · VORP {r.vorpBones ?? '—'} bones ·{' '}
          {r.ktcValue !== undefined ? (
            <>KTC {r.ktcValue}</>
          ) : (
            <>
              value {r.value.toFixed(2)} = wPPG {r.wppg.toFixed(2)} × contract{' '}
              {r.contractFactor.toFixed(2)} × age {r.ageFactor.toFixed(2)}
            </>
          )}
        </div>
        {r.vorp !== undefined && (
          <div className="mt-0.5">
            VORP +{r.vorp.toFixed(1)} /wk over {r.position} replacement
            {r.vorpSeason !== undefined && <> (through {r.vorpSeason})</>}
            {r.vorpRank !== undefined && <> · VORP rank #{r.vorpRank}</>}
          </div>
        )}
        <div className="mt-1 flex flex-wrap gap-1">
          {r.seasons.map((s) => (
            <span key={s.year} className="border rounded px-1.5 py-0.5 bg-white">
              {s.year}: {s.ppg.toFixed(2)} ppg ({s.totalPoints.toFixed(1)} pts / {s.weeksRostered}{' '}
              wk)
            </span>
          ))}
        </div>
      </div>
    ) : null;

  return (
    <PageShell
      title="Rankings"
      subtitle={`Bones, two lenses — KTC superflex market · ${vorpLabel} — click a column to re-rank · refreshed ${computedAt}`}
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {POSITIONS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPos(p)}
            className={`px-2 py-1 border rounded text-sm ${
              pos === p ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white hover:bg-gray-50'
            }`}
          >
            {p}
          </button>
        ))}
        <select
          className="ml-auto border rounded px-2 py-1 text-sm bg-white"
          value={franchiseFilter}
          onChange={(e) => setFranchiseFilter(e.target.value)}
        >
          <option value="ALL">All teams</option>
          <option value="FA">Free agents</option>
          {sortedFranchises.map((f) => (
            <option key={f._id} value={String(f._id)}>
              {f.name}
            </option>
          ))}
        </select>
      </div>
      <details className="mb-3 rounded border bg-white px-3 py-2 text-xs text-gray-600">
        <summary className="cursor-pointer font-medium text-gray-700">
          How are these bones calculated?
        </summary>
        <div className="mt-2 space-y-2">
          <p>
            Both columns are priced in the same currency: each lens is rescaled so the mean of its
            top three players is <strong>{TOP_ANCHOR_BONES} bones</strong> — the range the league
            actually pays at auction (the 2025 RFA peak bids were 86 and 90).
          </p>
          <p>
            <strong>Market</strong> is the dynasty market's opinion: KeepTradeCut <em>superflex</em>{' '}
            values, matched to our players and rescaled. It prices age, injury outlook, and future
            upside — everything a trade partner is thinking about.
          </p>
          <p>
            <strong>VORP</strong> is what BDL lineups say, from{' '}
            {vorpSeason !== undefined
              ? `completed seasons through ${vorpSeason}`
              : 'completed seasons'}{' '}
            (the current season never counts until it finishes):
          </p>
          <ol className="ml-4 list-decimal space-y-0.5">
            <li>
              <strong>Demand</strong> — how many of each position the league actually starts per
              week, measured from real lineups (superflex pushed QB demand from ~10 to ~21).
            </li>
            <li>
              <strong>Replacement</strong> — the player ranked {REPLACEMENT_BUFFER} spots past that
              cutoff: the guy you could stream instead.
            </li>
            <li>
              <strong>Production</strong> — points per week, recency-weighted, counted as the better
              of per-rostered-week and per-<em>started</em>-week (with {MIN_STARTS_FOR_RATE}+
              starts), so an injury year doesn't erase a player who delivers when he plays.
            </li>
            <li>
              <strong>VORP</strong> = production − replacement, then multiplied by contract years (+
              {Math.round(CONTRACT_FACTOR_PER_YEAR * 100)}% per year of team control).
            </li>
          </ol>
          <p>
            A big gap between the columns is the interesting part: high Market / low VORP is a name
            the league hasn't been paid by; high VORP / low Market is production the market is
            discounting.
          </p>
        </div>
      </details>
      <DataTable
        columns={columns}
        rows={filtered}
        rowKey={(r) => r.nflId}
        empty="No players match the current filters."
        onRowClick={(r) => setExpanded((cur) => (cur === r.nflId ? null : r.nflId))}
        expandedContent={breakdown}
      />
      <p className="mt-3 text-xs text-gray-400">
        Dynasty values via KeepTradeCut (keeptradecut.com)
      </p>
    </PageShell>
  );
}
